import type { Question } from './topics';
import { options, pick } from './util';

// ─────────────────────────────────────────────────────────────────────────────
// German, by rule. Present tense for verbs with no vowel change (no fahren,
// lesen, essen…). Two spelling rules, both applied:
//   • stem ends in d / t (or consonant + m/n, öffnen): an e before -st / -t
//       du arbeitest, er findet, ihr wartet
//   • stem ends in s / ß / z / x: du just adds -t
//       du tanzt, du heißt, du reist
// ─────────────────────────────────────────────────────────────────────────────

// "sie" is she AND they — labelled, so a prompt never has two right answers.
const PEOPLE = [['ich'], ['du'], ['er', 'es', 'sie (she)'], ['wir'], ['ihr'], ['sie (they)', 'Sie']];
const END = ['e', 'st', 't', 'en', 't', 'en'];

export const DE_VERBS: Record<string, string> = {
  machen: 'to do / make', spielen: 'to play', wohnen: 'to live', lernen: 'to learn', kaufen: 'to buy',
  sagen: 'to say', hören: 'to hear', fragen: 'to ask', kochen: 'to cook', suchen: 'to look for',
  brauchen: 'to need', glauben: 'to believe', lieben: 'to love', schwimmen: 'to swim', trinken: 'to drink',
  arbeiten: 'to work', finden: 'to find', reden: 'to talk', warten: 'to wait', antworten: 'to answer',
  öffnen: 'to open', tanzen: 'to dance', heißen: 'to be called', reisen: 'to travel',
};

export function dePresent(verb: string, i: number): string {
  const stem = verb.slice(0, -2);
  let end = END[i];
  // öffnen → öffnest, but schwimmen → schwimmst (a doubled mm/nn doesn't count).
  const needsE = /[dt]$/.test(stem) || /[^aeiouäöülrhmn][mn]$/.test(stem);
  if (needsE && (end === 'st' || end === 't')) end = 'e' + end;
  if (/(s|ß|z|x)$/.test(stem) && end === 'st') end = 't';
  return stem + end;
}

export function deRegular(n: number): Question[] {
  const verbs = Object.keys(DE_VERBS);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const verb = pick(verbs);
    const i = Math.floor(Math.random() * 6);
    const answer = dePresent(verb, i);
    const who = pick(PEOPLE[i]);
    const typed = Math.random() < 0.5;
    qs.push({
      key: `de_present:${verb}:${i}`, topic: 'de_present', kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Present tense · write the verb' : 'Present tense · choose the verb',
      prompt: `${who} ___ (${verb})`, hint: DE_VERBS[verb], answers: [answer],
      options: options(answer, [0, 1, 2, 3, 4, 5].map(j => dePresent(verb, j))),
    });
  }
  return qs;
}

const SEIN = ['bin', 'bist', 'ist', 'sind', 'seid', 'sind'];
const HABEN = ['habe', 'hast', 'hat', 'haben', 'habt', 'haben'];

export function deSeinHaben(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const sein = Math.random() < 0.5;
    const table = sein ? SEIN : HABEN;
    const i = Math.floor(Math.random() * 6);
    const typed = Math.random() < 0.4;
    qs.push({
      key: `de_sein_haben:${sein}:${i}`, topic: 'de_sein_haben', kind: typed ? 'type' : 'choice',
      instruction: `${sein ? 'Sein' : 'Haben'} · present tense`, prompt: `${pick(PEOPLE[i])} ___ (${sein ? 'sein' : 'haben'})`,
      hint: sein ? 'to be' : 'to have', answers: [table[i]],
      options: options(table[i], table),
    });
  }
  return qs;
}

type Noun = { w: string; g: 'm' | 'f' | 'n'; en: string; trap?: boolean; thing?: boolean };
const DE_NOUNS: Noun[] = [
  { w: 'Tisch', g: 'm', en: 'table', thing: true }, { w: 'Stuhl', g: 'm', en: 'chair', thing: true },
  { w: 'Hund', g: 'm', en: 'dog', thing: true }, { w: 'Apfel', g: 'm', en: 'apple', thing: true },
  { w: 'Zug', g: 'm', en: 'train', thing: true }, { w: 'Kaffee', g: 'm', en: 'coffee', thing: true },
  { w: 'Tee', g: 'm', en: 'tea', thing: true }, { w: 'Computer', g: 'm', en: 'computer', thing: true },
  { w: 'Lehrer', g: 'm', en: 'teacher (m)' }, { w: 'Tag', g: 'm', en: 'day' }, { w: 'Monat', g: 'm', en: 'month' },
  { w: 'Kopf', g: 'm', en: 'head' }, { w: 'Fuß', g: 'm', en: 'foot' }, { w: 'Mond', g: 'm', en: 'moon' },
  { w: 'Löffel', g: 'm', en: 'spoon', thing: true, trap: true },
  { w: 'Katze', g: 'f', en: 'cat', thing: true }, { w: 'Tür', g: 'f', en: 'door', thing: true },
  { w: 'Lampe', g: 'f', en: 'lamp', thing: true }, { w: 'Banane', g: 'f', en: 'banana', thing: true },
  { w: 'Zeitung', g: 'f', en: 'newspaper', thing: true }, { w: 'Milch', g: 'f', en: 'milk', thing: true },
  { w: 'Schule', g: 'f', en: 'school' }, { w: 'Stadt', g: 'f', en: 'city' }, { w: 'Woche', g: 'f', en: 'week' },
  { w: 'Sonne', g: 'f', en: 'sun' }, { w: 'Musik', g: 'f', en: 'music' }, { w: 'Hand', g: 'f', en: 'hand' },
  { w: 'Nase', g: 'f', en: 'nose' }, { w: 'Lehrerin', g: 'f', en: 'teacher (f)' },
  { w: 'Gabel', g: 'f', en: 'fork', thing: true, trap: true },
  { w: 'Buch', g: 'n', en: 'book', thing: true }, { w: 'Auto', g: 'n', en: 'car', thing: true },
  { w: 'Haus', g: 'n', en: 'house', thing: true }, { w: 'Brot', g: 'n', en: 'bread', thing: true },
  { w: 'Wasser', g: 'n', en: 'water', thing: true }, { w: 'Fenster', g: 'n', en: 'window', thing: true },
  { w: 'Handy', g: 'n', en: 'cell phone', thing: true }, { w: 'Bett', g: 'n', en: 'bed', thing: true },
  { w: 'Land', g: 'n', en: 'country' }, { w: 'Jahr', g: 'n', en: 'year' }, { w: 'Auge', g: 'n', en: 'eye' },
  { w: 'Kind', g: 'n', en: 'child' },
  { w: 'Messer', g: 'n', en: 'knife', thing: true, trap: true },
  { w: 'Mädchen', g: 'n', en: 'girl', trap: true },
];

const NOM = { m: 'der', f: 'die', n: 'das' } as const;
const AKK = { m: 'den', f: 'die', n: 'das' } as const;

export function deArticles(n: number): Question[] {
  const traps = DE_NOUNS.filter(x => x.trap);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = Math.random() < 0.3 ? pick(traps) : pick(DE_NOUNS);
    qs.push({
      key: `de_articles:${x.w}`, topic: 'de_articles', kind: 'choice', instruction: 'Der, die or das?',
      prompt: `___ ${x.w}`, hint: x.en, options: ['der', 'die', 'das'], answers: [NOM[x.g]],
      ...(x.w === 'Mädchen' ? { why: 'Every noun ending in -chen is das, even das Mädchen' } : {}),
      ...(x.trap && x.w !== 'Mädchen' ? { why: `der Löffel, die Gabel, das Messer: a famous trio to memorise` } : {}),
    });
  }
  return qs;
}

const FRAMES = ['Ich sehe ___ {n}.', 'Wir kaufen ___ {n}.', 'Hast du ___ {n}?', 'Ich brauche ___ {n}.', 'Sie sucht ___ {n}.'];

export function deAccusative(n: number): Question[] {
  const things = DE_NOUNS.filter(x => x.thing);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = pick(things);
    const frame = pick(FRAMES);
    qs.push({
      key: `de_akk:${x.w}:${frame}`, topic: 'de_accusative', kind: 'choice', instruction: 'Accusative: den, die or das?',
      prompt: frame.replace('{n}', x.w), hint: `${NOM[x.g]} ${x.w}: ${x.en}`,
      options: ['der', 'den', 'die', 'das'], answers: [AKK[x.g]],
      why: x.g === 'm' ? `Masculine: der → den in the accusative` : `${NOM[x.g]} doesn't change in the accusative`,
    });
  }
  return qs;
}
